import { VFC } from 'react'
import { PremiumContribution } from 'src/models/PremiumContribution/index'
import styled from 'styled-components'
import { defaultShadow, _filterbg } from 'src/styles/colors'
import { fontWeightBold, fontWeightRegular } from 'src/styles/font'
import {
  PremiumContributionPriceIcon,
  PremiumContributionUserIcon,
  PremiumContributionBXIcon,
  PremiumContributionCardIcon,
  PremiumContributionCalendarIcon,
  ContentsMoreIcon,
} from 'src/assets/svgs'

type PremiumContributionCardProps = {
  contribution: PremiumContribution
  showProfile: (id: number) => void
}

export const PremiumContributionCard: VFC<PremiumContributionCardProps> = ({
  contribution,
  showProfile,
}) => {
  const showDetailButton =
    contribution.isApplied || contribution.shouldDisplayCloseLabel
  return (
    <Card>
      <CardHeader>
        <PublishStatusBadge isClosed={contribution.shouldDisplayCloseLabel}>
          {contribution.shouldDisplayCloseLabel ? '募集終了' : '募集中'}
        </PublishStatusBadge>
        {contribution.isApplied && (
          <EntryStatusBadge>エントリー済</EntryStatusBadge>
        )}
      </CardHeader>

      <CardContent>
        <Title>{contribution.title}</Title>
        <Content>{contribution.content}</Content>

        <Details>
          <DetailItem>
            <PremiumContributionPriceIcon />
            <Label>予算</Label>
            <Value>{contribution.displayBudgetText}</Value>
          </DetailItem>
          <DetailItem>
            <PremiumContributionUserIcon />
            <Label>従業員数</Label>
            <Value>{contribution.employeeNumberText}</Value>
          </DetailItem>
          <DetailItem>
            <PremiumContributionBXIcon />
            <Label>業界・業種</Label>
            <Value>{contribution.typeOfBusiness?.name ?? 'ー'}</Value>
          </DetailItem>
          <DetailItem>
            <PremiumContributionCardIcon />
            <Label>役職</Label>
            <Value>{contribution.positionText ?? 'ー'}</Value>
          </DetailItem>
          <DetailItem>
            <PremiumContributionCalendarIcon />
            <Label>掲載期間:</Label>
            <Value>
              {contribution.daysLeftText}
              <ValueDates>
                ({contribution.startAt.format('YYYY/MM/DD')}~{' '}
                {contribution.endAt.format('YYYY/MM/DD')})
              </ValueDates>
            </Value>
          </DetailItem>
        </Details>

        <NeedsTags>
          {contribution.contentTags.map((tag) => (
            <NeedsTag as="span" key={tag.id}>
              # {tag.name}
            </NeedsTag>
          ))}
        </NeedsTags>
      </CardContent>

      <CardFooter>
        <MoreButton
          isApplied={showDetailButton}
          onClick={() => showProfile(contribution.id)}
        >
          {showDetailButton ? (
            '詳細を見る'
          ) : (
            <>
              募集内容の詳細を確認し
              <br />
              エントリーする
            </>
          )}
          <ContentsMoreIcon />
        </MoreButton>
      </CardFooter>
    </Card>
  )
}

const Card = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;
  box-shadow: ${defaultShadow};
  width: calc(100% / 3 - 16px);
  overflow: hidden;
  padding: 40px 24px 110px;
  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 40px 20px 90px;
  }
`

const CardHeader = styled.div``

const CardContent = styled.div``

const CardFooter = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 24px;
  @media screen and (max-width: 768px) {
    left: 20px;
    right: 20px;
    bottom: 20px;
  }
`

const PublishStatusBadge = styled('div')<{ isClosed: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  padding: 4px 16px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  font-size: 14px;
  font-weight: ${fontWeightBold};
  background: ${(props) => (props.isClosed ? 'white' : '#02B7B0')};
  color: ${(props) => (props.isClosed ? '#B2B3B6' : 'white')};
  border: 1px solid ${(props) => (props.isClosed ? '#B2B3B6' : '#02b7b0')};
`

const EntryStatusBadge = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  padding: 4px 16px;
  border-bottom-right-radius: 8px;
  font-size: 14px;
  font-weight: ${fontWeightBold};
  background: #e4e4e4;
  color: #393e4c;
  border: 1px solid #e4e4e4;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: ${fontWeightBold};
  color: #393e4c;
  margin: 0 0 8px 0;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  display: -webkit-box;
  min-height: 62px;
  @media screen and (max-width: 768px) {
    font-size: 16px;
    min-height: initial;
    display: block;
  }
`

const Content = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 16px 0;
  color: #393e4c;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  display: -webkit-box;
  min-height: 63px;
  @media screen and (max-width: 768px) {
    min-height: initial;
    display: block;
    margin-bottom: 8px;
  }
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  @media screen and (max-width: 768px) {
    margin-bottom: 8px;
    gap: 4px;
  }
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
`

const Label = styled.span`
  font-size: 10px;
  font-weight: ${fontWeightBold};
  color: #b2b3b6;
  width: 50px;
  flex-shrink: 0;
  margin-left: 2px;
`

const Value = styled.span`
  font-size: 12px;
  font-weight: ${fontWeightBold};
  color: #393e4c;
  margin-left: 8px;
`

const ValueDates = styled.span`
  font-size: 10px;
  font-weight: ${fontWeightRegular};
  color: #b2b3b6;
  margin-left: 4px;
`

const MoreButton = styled('button')<{ isApplied: boolean }>`
  display: block;
  width: 100%;
  font-size: 16px;
  height: 62px;
  padding: 12px 26px;
  line-height: 1.2;
  border: 1px solid ${(props) => (props.isApplied ? '#393E4C' : '#F29600')};
  color: ${(props) => (props.isApplied ? '#393E4C' : '#F29600')};
  font-weight: ${fontWeightBold};
  text-align: center;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  svg {
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    path {
      stroke: ${(props) => (props.isApplied ? '#393E4C' : '#F29600')};
    }
  }
`

const NeedsTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
`
const NeedsTag = styled.span`
  font-size: 11px;
  font-weight: ${fontWeightRegular};
  color: #393e4c;
  background: #e4e4e4;
  padding: 4px;
`
